import type { ChartNode, QrNode, ShapeNode, TemplateNode } from "../builder";
import type { LibraryTemplate } from "../library";
import { ellipse, icon, line, logo, photo, rect, text } from "./shorthands";

/**
 * Showcase templates: a presentation with speaker notes, templates for bulk
 * creation ({{Platzhalter}} in texts and QR codes), an infographic and an
 * annual report page with charts, arrows and icons.
 */

const chart = (props: Omit<ChartNode, "kind">): ChartNode => ({ kind: "chart", ...props });
const qr = (props: Omit<QrNode, "kind">): QrNode => ({ kind: "qr", ...props });
/** A line with an arrowhead at its end. */
const arrow = (
  x: number,
  y: number,
  w: number,
  color: string,
  width = 4,
  extra: Partial<ShapeNode> = {},
): ShapeNode => ({ ...line(x, y, w, color, width), lineEnd: "arrow", lineRound: true, ...extra });

// ---------------------------------------------------------------------------
// Presentation 16:9 (reference page 1080 × 607.5)
// ---------------------------------------------------------------------------

/** Slide title with a small accent bar. */
function slideTitle(title: string, kicker: string): TemplateNode[] {
  return [
    rect({ x: 60, y: 52, w: 44, h: 6, fill: "accent", radius: "full", name: "Akzent" }),
    text({ text: kicker, style: "subline", size: 16, color: "text-muted", x: 60, y: 68, w: 700 }),
    text({ text: title, style: "heading-2", size: 38, color: "text", x: 60, y: 92, w: 900 }),
  ];
}

const footer = (page: string): TemplateNode[] => [
  logo({ x: 60, y: 555, w: 120, h: 30, on: "background" }),
  text({
    text: page,
    style: "caption",
    size: 13,
    color: "text-muted",
    x: 860,
    y: 562,
    w: 160,
    align: "right",
  }),
];

const PRESENTATION: LibraryTemplate = {
  id: "praesentation-firma-16-9",
  title: "Präsentation: Unternehmen",
  description:
    "Zehn Folien mit Agenda, Kennzahlen-Diagramm, Zeitstrahl, Team, Vergleich und QR-Kontakt – mit Sprechernotizen für den Präsentationsmodus",
  presetId: "presentation",
  topic: "produkt",
  style: "klar",
  tags: ["präsentation", "folien", "slides", "pitch", "vortrag", "meeting", "diagramm"],
  pages: [
    {
      name: "Titel",
      notes:
        "Begrüßung: kurz vorstellen, worum es heute geht und wie lange es dauert. Fragen gerne am Ende.",
      background: "primary",
      nodes: [
        ellipse({ x: 700, y: -160, w: 560, h: 560, fill: "on:primary/10", name: "Kreis" }),
        ellipse({ x: 820, y: 330, w: 360, h: 360, fill: "accent", name: "Akzentkreis" }),
        logo({ x: 60, y: 50, w: 180, h: 50, on: "primary" }),
        text({
          text: "Strategie 2027",
          style: "heading-1",
          size: 72,
          color: "on:primary",
          x: 60,
          y: 210,
          w: 700,
        }),
        text({
          text: "Wachstum, Team und die nächsten Schritte",
          style: "subline",
          size: 26,
          color: "on:primary/80",
          x: 60,
          y: 310,
          w: 680,
        }),
        text({
          text: "Jahresauftakt · 12. Januar 2027",
          style: "caption",
          size: 16,
          color: "on:primary/70",
          x: 60,
          y: 520,
          w: 500,
        }),
      ],
    },
    {
      name: "Agenda",
      notes: "Die vier Themen kurz anreißen – Details kommen auf den nächsten Folien.",
      background: "background",
      nodes: [
        ...slideTitle("Agenda", "HEUTE"),
        ...["Rückblick 2026", "Zahlen & Fakten", "Meilensteine", "Ausblick & Team"].flatMap(
          (item, index) => [
            ellipse({
              x: 60 + index * 245,
              y: 230,
              w: 64,
              h: 64,
              fill: index === 0 ? "accent" : "primary",
              name: `Nummer ${index + 1}`,
            }),
            text({
              text: String(index + 1),
              style: "heading-2",
              size: 28,
              color: index === 0 ? "on:accent" : "on:primary",
              x: 60 + index * 245,
              y: 230,
              w: 64,
              h: 64,
              align: "center",
            }),
            text({
              text: item,
              style: "subline",
              size: 22,
              color: "text",
              x: 60 + index * 245,
              y: 320,
              w: 210,
            }),
          ],
        ),
        ...footer("2"),
      ],
    },
    {
      name: "Kennzahlen",
      notes:
        "Umsatz je Quartal im Vergleich zum Vorjahr. Die Daten lassen sich rechts im Panel bearbeiten oder aus Excel einfügen.",
      background: "background",
      nodes: [
        ...slideTitle("Umsatz wächst jedes Quartal", "ZAHLEN & FAKTEN"),
        chart({
          chart: "column",
          x: 60,
          y: 160,
          w: 600,
          h: 370,
          labels: ["Q1", "Q2", "Q3", "Q4"],
          series: [
            { name: "2025", values: [4.2, 5.1, 4.8, 6.3] },
            { name: "2026", values: [4.8, 5.7, 6.1, 7.2] },
          ],
          unit: "Mio. €",
          decimals: 1,
          legend: "top",
          fontSize: 15,
          name: "Umsatz-Diagramm",
        }),
        rect({ x: 700, y: 170, w: 320, h: 160, fill: "surface", radius: "lg", name: "Karte" }),
        text({
          text: "+14 %",
          style: "heading-1",
          size: 54,
          color: "primary",
          x: 725,
          y: 190,
          w: 280,
        }),
        text({
          text: "Umsatz gegenüber dem Vorjahr",
          style: "body",
          size: 17,
          color: "text-muted",
          x: 725,
          y: 265,
          w: 280,
        }),
        chart({
          chart: "progress",
          x: 740,
          y: 350,
          w: 170,
          h: 170,
          labels: ["Ziel"],
          series: [{ name: "Ziel", values: [92] }],
          unit: "%",
          hole: 0.72,
          fontSize: 13,
          name: "Zielerreichung",
        }),
        text({
          text: "des Jahresziels erreicht",
          style: "body",
          size: 16,
          color: "text-muted",
          x: 920,
          y: 420,
          w: 120,
        }),
        ...footer("3"),
      ],
    },
    {
      name: "Zeitstrahl",
      notes: "Die wichtigsten Meilensteine des Jahres – bei Bedarf eigene Termine eintragen.",
      background: "background",
      nodes: [
        ...slideTitle("Meilensteine 2026", "RÜCKBLICK"),
        arrow(60, 320, 960, "primary", 4),
        ...(
          [
            ["Jan", "Neues Büro in Köln"],
            ["Apr", "Produktstart"],
            ["Jul", "100. Kunde"],
            ["Okt", "Team verdoppelt"],
          ] as const
        ).flatMap(([month, label], index) => [
          ellipse({
            x: 110 + index * 240,
            y: 304,
            w: 36,
            h: 36,
            fill: index % 2 === 0 ? "primary" : "accent",
            name: `Punkt ${index + 1}`,
          }),
          text({
            text: month,
            style: "heading-2",
            size: 26,
            color: "primary",
            x: 60 + index * 240,
            y: 240,
            w: 136,
            align: "center",
          }),
          text({
            text: label,
            style: "body",
            size: 18,
            color: "text",
            x: 50 + index * 240,
            y: 365,
            w: 156,
            align: "center",
          }),
        ]),
        ...footer("4"),
      ],
    },
    {
      name: "Zitat",
      notes: "Kundenstimme vorlesen und kurz einordnen, warum sie uns wichtig ist.",
      background: "surface",
      nodes: [
        icon({
          icon: "quotes",
          x: 60,
          y: 90,
          w: 90,
          h: 90,
          color: "accent",
          name: "Anführungszeichen",
        }),
        text({
          text: "„Die Zusammenarbeit war schnell, unkompliziert und hat unser Team spürbar entlastet.“",
          style: "quote",
          size: 40,
          color: "text",
          x: 60,
          y: 200,
          w: 900,
        }),
        text({
          text: "Maria Beispiel · Geschäftsführerin, Musterfirma GmbH",
          style: "subline",
          size: 18,
          color: "text-muted",
          x: 60,
          y: 430,
          w: 900,
        }),
        ...footer("5"),
      ],
    },
    {
      name: "Team",
      notes: "Fotos per Drag & Drop auf die Platzhalter ziehen. Kurz jede Person vorstellen.",
      background: "background",
      nodes: [
        ...slideTitle("Unser Team", "MENSCHEN"),
        ...(
          [
            ["Anna Muster", "Geschäftsführung"],
            ["Ben Beispiel", "Vertrieb"],
            ["Clara Demo", "Design"],
            ["David Probe", "Technik"],
          ] as const
        ).flatMap(([name, role], index) => [
          photo({ x: 60 + index * 245, y: 170, w: 200, h: 200, ellipse: true }),
          text({
            text: name,
            style: "subline",
            size: 20,
            color: "text",
            x: 40 + index * 245,
            y: 390,
            w: 240,
            align: "center",
          }),
          text({
            text: role,
            style: "caption",
            size: 16,
            color: "text-muted",
            x: 40 + index * 245,
            y: 420,
            w: 240,
            align: "center",
          }),
        ]),
        ...footer("6"),
      ],
    },
    {
      name: "Vergleich",
      notes: "Vorher/Nachher: Was sich für Kundinnen und Kunden konkret verbessert hat.",
      background: "background",
      nodes: [
        ...slideTitle("Vorher und nachher", "VERGLEICH"),
        rect({ x: 60, y: 170, w: 420, h: 340, fill: "surface", radius: "lg", name: "Vorher" }),
        rect({ x: 600, y: 170, w: 420, h: 340, fill: "primary", radius: "lg", name: "Nachher" }),
        arrow(495, 340, 90, "accent", 6),
        icon({ icon: "x-circle", x: 90, y: 195, w: 48, h: 48, color: "text-muted" }),
        icon({ icon: "check-circle", x: 630, y: 195, w: 48, h: 48, color: "on:primary" }),
        text({
          text: "Vorher",
          style: "heading-2",
          size: 30,
          color: "text",
          x: 150,
          y: 200,
          w: 300,
        }),
        text({
          text: "Nachher",
          style: "heading-2",
          size: 30,
          color: "on:primary",
          x: 690,
          y: 200,
          w: 300,
        }),
        text({
          text: "• 5 Tage bis zum Angebot\n• Papierformulare\n• Rückfragen per Telefon",
          style: "body",
          size: 20,
          color: "text",
          x: 90,
          y: 270,
          w: 370,
        }),
        text({
          text: "• Angebot am selben Tag\n• Alles online\n• Status jederzeit sichtbar",
          style: "body",
          size: 20,
          color: "on:primary",
          x: 630,
          y: 270,
          w: 370,
        }),
        ...footer("7"),
      ],
    },
    {
      name: "Marktanteile",
      notes: "Wo unsere Kundinnen und Kunden herkommen – Empfehlungen sind der größte Kanal.",
      background: "background",
      nodes: [
        ...slideTitle("Woher neue Kunden kommen", "VERTRIEB"),
        chart({
          chart: "donut",
          x: 60,
          y: 150,
          w: 480,
          h: 390,
          labels: ["Empfehlung", "Social Media", "Website", "Messen"],
          series: [{ name: "Anteil", values: [41, 27, 20, 12] }],
          unit: "%",
          showValues: true,
          legend: "right",
          fontSize: 16,
          name: "Ringdiagramm",
        }),
        chart({
          chart: "bar",
          x: 590,
          y: 160,
          w: 430,
          h: 360,
          title: "Zufriedenheit (1–5)",
          labels: ["Service", "Preis", "Qualität", "Tempo"],
          series: [{ name: "Note", values: [4.8, 4.1, 4.6, 4.4] }],
          decimals: 1,
          max: 5,
          showValues: true,
          gridlines: false,
          fontSize: 15,
          name: "Balkendiagramm",
        }),
        ...footer("8"),
      ],
    },
    {
      name: "Ausblick",
      notes: "Drei Ziele für das kommende Jahr – jeweils mit einem Satz begründen.",
      background: "background",
      nodes: [
        ...slideTitle("Ausblick 2027", "NÄCHSTE SCHRITTE"),
        ...(
          [
            ["trend-up", "Wachsen", "Zwei neue Regionen und 30 % mehr Umsatz."],
            ["lightbulb", "Entwickeln", "Drei neue Produkte bis zum Sommer."],
            ["heart", "Begeistern", "Kundenzufriedenheit auf 4,9 steigern."],
          ] as const
        ).flatMap(([name, title, body], index) => [
          rect({
            x: 60 + index * 330,
            y: 170,
            w: 300,
            h: 330,
            fill: "surface",
            radius: "lg",
            name: `Karte ${index + 1}`,
          }),
          icon({ icon: name, x: 90 + index * 330, y: 200, w: 60, h: 60, color: "primary" }),
          text({
            text: title,
            style: "heading-2",
            size: 28,
            color: "text",
            x: 90 + index * 330,
            y: 285,
            w: 240,
          }),
          text({
            text: body,
            style: "body",
            size: 18,
            color: "text-muted",
            x: 90 + index * 330,
            y: 335,
            w: 240,
          }),
        ]),
        ...footer("9"),
      ],
    },
    {
      name: "Danke",
      notes: "Danke sagen, auf den QR-Code zum Kontakt hinweisen und Fragen sammeln.",
      background: "primary",
      nodes: [
        ellipse({ x: -160, y: 330, w: 520, h: 520, fill: "on:primary/10", name: "Kreis" }),
        text({
          text: "Danke!",
          style: "heading-1",
          size: 90,
          color: "on:primary",
          x: 60,
          y: 150,
          w: 600,
        }),
        text({
          text: "Fragen? Wir freuen uns auf das Gespräch.\nhallo@beispiel.de · 0221 123 456",
          style: "subline",
          size: 22,
          color: "on:primary/85",
          x: 60,
          y: 280,
          w: 600,
        }),
        rect({ x: 770, y: 150, w: 250, h: 250, fill: "#ffffff", radius: "md", name: "QR-Fläche" }),
        qr({ value: "https://beispiel.de/kontakt", x: 785, y: 165, size: 220, name: "Kontakt-QR" }),
        text({
          text: "Kontakt scannen",
          style: "caption",
          size: 16,
          color: "on:primary/80",
          x: 770,
          y: 415,
          w: 250,
          align: "center",
        }),
        logo({ x: 60, y: 520, w: 150, h: 44, on: "primary" }),
      ],
    },
  ],
};

// ---------------------------------------------------------------------------
// Bulk creation („Serie erstellen“): {{Name}}, {{Firma}}, {{Code}}
// ---------------------------------------------------------------------------

const SERIES: LibraryTemplate[] = [
  {
    id: "gutschein-serie-a6",
    title: "Gutschein (Serie)",
    description:
      "Gutschein mit {{Name}} und {{Code}} – mit „Serie erstellen“ aus einer Tabelle für viele Personen auf einmal",
    presetId: "a6",
    topic: "angebot",
    style: "elegant",
    tags: ["gutschein", "serie", "serienbrief", "platzhalter", "geschenk", "code"],
    pages: [
      {
        name: "Gutschein",
        background: "background",
        nodes: [
          rect({ x: -30, y: -30, w: 1140, h: 700, fill: "primary", name: "Farbfläche" }),
          icon({ icon: "sparkle", x: 460, y: 120, w: 160, h: 160, color: "accent", name: "Stern" }),
          text({
            text: "GUTSCHEIN",
            style: "subline",
            size: 44,
            color: "on:primary/80",
            x: 90,
            y: 330,
            w: 900,
            align: "center",
          }),
          text({
            text: "für {{Name}}",
            style: "heading-1",
            size: 96,
            color: "on:primary",
            x: 90,
            y: 410,
            w: 900,
            align: "center",
          }),
          text({
            text: "im Wert von",
            style: "body",
            size: 40,
            color: "text-muted",
            x: 90,
            y: 760,
            w: 900,
            align: "center",
          }),
          text({
            text: "50 €",
            style: "heading-1",
            size: 170,
            color: "primary",
            x: 90,
            y: 820,
            w: 900,
            align: "center",
          }),
          line(240, 1120, 600, "text/30", 2),
          text({
            text: "Code: {{Code}} · gültig bis 31.12.2027",
            style: "caption",
            size: 32,
            color: "text-muted",
            x: 90,
            y: 1160,
            w: 900,
            align: "center",
          }),
          logo({ x: 390, y: 1300, w: 300, h: 100, on: "background", align: "center" }),
        ],
      },
    ],
  },
  {
    id: "namensschild-serie-a6",
    title: "Namensschild (Serie)",
    description:
      "Namensschild mit {{Name}} und {{Firma}} – mit „Serie erstellen“ für alle Gäste aus einer Teilnehmerliste",
    presetId: "a6",
    topic: "event",
    style: "klar",
    tags: ["namensschild", "badge", "serie", "platzhalter", "konferenz", "teilnehmer"],
    pages: [
      {
        name: "Namensschild",
        background: "background",
        nodes: [
          rect({ x: -30, y: -30, w: 1140, h: 330, fill: "primary", name: "Kopf" }),
          logo({ x: 90, y: 90, w: 360, h: 120, on: "primary" }),
          text({
            text: "ZUKUNFTSTAG 2027",
            style: "subline",
            size: 36,
            color: "on:primary/85",
            x: 560,
            y: 130,
            w: 430,
            align: "right",
          }),
          text({
            text: "HALLO, ICH BIN",
            style: "subline",
            size: 40,
            color: "text-muted",
            x: 90,
            y: 480,
            w: 900,
            align: "center",
          }),
          text({
            text: "{{Name}}",
            style: "heading-1",
            size: 130,
            color: "text",
            x: 60,
            y: 560,
            w: 960,
            align: "center",
          }),
          text({
            text: "{{Firma}}",
            style: "subline",
            size: 52,
            color: "primary",
            x: 60,
            y: 800,
            w: 960,
            align: "center",
          }),
          rect({ x: -30, y: 1330, w: 1140, h: 222, fill: "accent", name: "Fußleiste" }),
        ],
      },
    ],
  },
  {
    id: "ticket-serie-din-lang",
    title: "Event-Ticket mit QR (Serie)",
    description:
      "Ticket mit {{Name}}, {{Code}} und QR-Code zum Einlass – mit „Serie erstellen“ je Gast ein eigener Code",
    presetId: "din-lang",
    topic: "event",
    style: "nacht",
    tags: ["ticket", "eintrittskarte", "qr", "serie", "platzhalter", "einlass", "event"],
    pages: [
      {
        name: "Ticket",
        background: "background",
        nodes: [
          photo({ x: -30, y: -30, w: 1140, h: 900 }),
          rect({ x: -30, y: 830, w: 1140, h: 1500, fill: "primary", name: "Farbfläche" }),
          text({
            text: "SOMMERNACHT\nOPEN AIR",
            style: "heading-1",
            size: 120,
            color: "on:primary",
            x: 90,
            y: 920,
            w: 900,
          }),
          text({
            text: "Sa, 18. Juli 2027 · Einlass 18 Uhr\nStadtpark Köln",
            style: "subline",
            size: 44,
            color: "on:primary/85",
            x: 90,
            y: 1230,
            w: 900,
          }),
          line(90, 1420, 900, "on:primary/40", 3),
          text({
            text: "TICKET FÜR",
            style: "caption",
            size: 34,
            color: "on:primary/70",
            x: 90,
            y: 1480,
            w: 500,
          }),
          text({
            text: "{{Name}}",
            style: "heading-2",
            size: 70,
            color: "on:primary",
            x: 90,
            y: 1530,
            w: 520,
          }),
          text({
            text: "Nr. {{Code}}",
            style: "body",
            size: 38,
            color: "on:primary/80",
            x: 90,
            y: 1640,
            w: 520,
          }),
          rect({
            x: 640,
            y: 1480,
            w: 350,
            h: 350,
            fill: "#ffffff",
            radius: "md",
            name: "QR-Fläche",
          }),
          qr({
            value: "https://example.de/t/{{Code}}",
            x: 660,
            y: 1500,
            size: 310,
            name: "Einlass-QR",
          }),
          logo({ x: 90, y: 2020, w: 320, h: 110, on: "primary" }),
        ],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Infographic (story) and annual report page (A4)
// ---------------------------------------------------------------------------

const INFOGRAPHICS: LibraryTemplate[] = [
  {
    id: "infografik-story",
    title: "Infografik: Zahlen im Überblick",
    description: "Story mit Kennzahlen, Ring- und Säulendiagramm, Pfeilen und Icons",
    presetId: "story",
    topic: "wissen",
    style: "laut",
    tags: ["infografik", "statistik", "diagramm", "zahlen", "fakten", "umfrage"],
    pages: [
      {
        name: "Infografik",
        background: "background",
        nodes: [
          rect({ x: -30, y: -30, w: 1140, h: 520, fill: "primary", name: "Kopf" }),
          text({
            text: "SO ARBEITET DEUTSCHLAND",
            style: "subline",
            size: 40,
            color: "on:primary/80",
            x: 80,
            y: 230,
            w: 920,
          }),
          text({
            text: "Homeoffice in Zahlen",
            style: "heading-1",
            size: 92,
            color: "on:primary",
            x: 80,
            y: 290,
            w: 920,
          }),
          ...(
            [
              ["globe", "58 %", "arbeiten teilweise\nvon zu Hause"],
              ["timer", "2,4", "Tage pro Woche\nim Schnitt"],
              ["smiley", "81 %", "sind damit\nzufrieden"],
            ] as const
          ).flatMap(([name, value, label], index) => [
            icon({ icon: name, x: 110 + index * 320, y: 560, w: 90, h: 90, color: "accent" }),
            text({
              text: value,
              style: "heading-1",
              size: 80,
              color: "primary",
              x: 80 + index * 320,
              y: 670,
              w: 300,
            }),
            text({
              text: label,
              style: "body",
              size: 32,
              color: "text-muted",
              x: 80 + index * 320,
              y: 780,
              w: 300,
            }),
          ]),
          chart({
            chart: "donut",
            x: 80,
            y: 950,
            w: 440,
            h: 520,
            title: "Lieblingsort",
            labels: ["Küche", "Arbeitszimmer", "Wohnzimmer", "Café"],
            series: [{ name: "Anteil", values: [22, 46, 24, 8] }],
            unit: "%",
            showValues: true,
            legend: "bottom",
            fontSize: 26,
            name: "Ringdiagramm",
          }),
          arrow(540, 1200, 90, "accent", 8),
          chart({
            chart: "column",
            x: 650,
            y: 950,
            w: 360,
            h: 520,
            title: "Tage / Woche",
            labels: ["2020", "2023", "2026"],
            series: [{ name: "Tage", values: [1.1, 2.0, 2.4] }],
            decimals: 1,
            showValues: true,
            gridlines: false,
            axis: true,
            fontSize: 26,
            name: "Säulendiagramm",
          }),
          text({
            text: "Quelle: Beispielumfrage 2026, 1.000 Befragte",
            style: "caption",
            size: 26,
            color: "text-muted",
            x: 80,
            y: 1560,
            w: 920,
          }),
          logo({ x: 80, y: 1640, w: 260, h: 80, on: "background" }),
        ],
      },
    ],
  },
  {
    id: "jahresbericht-a4",
    title: "Jahresbericht-Seite A4",
    description: "Berichtsseite mit Kennzahlen, Linien- und Balkendiagramm und kurzem Text",
    presetId: "a4",
    topic: "wissen",
    style: "elegant",
    tags: ["jahresbericht", "geschäftsbericht", "bericht", "diagramm", "kennzahlen", "report"],
    pages: [
      {
        name: "Bericht",
        background: "background",
        nodes: [
          logo({ x: 90, y: 90, w: 260, h: 80, on: "background" }),
          text({
            text: "JAHRESBERICHT 2026",
            style: "subline",
            size: 26,
            color: "text-muted",
            x: 600,
            y: 115,
            w: 390,
            align: "right",
          }),
          line(90, 200, 900, "text/20", 2),
          text({
            text: "Ein Jahr voller Wachstum",
            style: "heading-1",
            size: 70,
            color: "text",
            x: 90,
            y: 240,
            w: 900,
          }),
          text({
            text: "Wir haben unseren Umsatz deutlich gesteigert, zwei neue Standorte eröffnet und unser Team auf 48 Menschen vergrößert. Die wichtigsten Zahlen im Überblick.",
            style: "body",
            size: 28,
            color: "text-muted",
            x: 90,
            y: 340,
            w: 900,
          }),
          ...(
            [
              ["7,2 Mio. €", "Umsatz"],
              ["+14 %", "Wachstum"],
              ["48", "Mitarbeitende"],
            ] as const
          ).flatMap(([value, label], index) => [
            rect({
              x: 90 + index * 310,
              y: 510,
              w: 280,
              h: 180,
              fill: "surface",
              radius: "md",
              name: `Kennzahl ${index + 1}`,
            }),
            text({
              text: value,
              style: "heading-2",
              size: 50,
              color: "primary",
              x: 115 + index * 310,
              y: 540,
              w: 240,
            }),
            text({
              text: label,
              style: "caption",
              size: 26,
              color: "text-muted",
              x: 115 + index * 310,
              y: 620,
              w: 240,
            }),
          ]),
          chart({
            chart: "line",
            x: 90,
            y: 750,
            w: 900,
            h: 420,
            title: "Umsatz je Monat (Tsd. €)",
            labels: [
              "Jan",
              "Feb",
              "Mär",
              "Apr",
              "Mai",
              "Jun",
              "Jul",
              "Aug",
              "Sep",
              "Okt",
              "Nov",
              "Dez",
            ],
            series: [
              {
                name: "2025",
                values: [480, 470, 510, 530, 520, 560, 540, 550, 600, 610, 650, 700],
              },
              {
                name: "2026",
                values: [520, 540, 580, 600, 610, 650, 640, 660, 690, 720, 760, 830],
              },
            ],
            legend: "top",
            fontSize: 22,
            name: "Liniendiagramm",
          }),
          chart({
            chart: "bar",
            x: 90,
            y: 1210,
            w: 900,
            h: 250,
            title: "Umsatz nach Bereich (Mio. €)",
            labels: ["Beratung", "Software", "Schulungen"],
            series: [{ name: "Umsatz", values: [3.1, 2.6, 1.5] }],
            decimals: 1,
            showValues: true,
            gridlines: false,
            fontSize: 22,
            name: "Balkendiagramm",
          }),
          text({
            text: "Seite 4",
            style: "caption",
            size: 22,
            color: "text-muted",
            x: 690,
            y: 1470,
            w: 300,
            align: "right",
          }),
        ],
      },
    ],
  },
];

export const SHOWCASE: LibraryTemplate[] = [PRESENTATION, ...SERIES, ...INFOGRAPHICS];
